<template>
  <v-card>
    <v-progress-linear
      absolute
      :active="loading"
      indeterminate
      color="primary"
      bottom
    ></v-progress-linear>
    <v-card-title></v-card-title>

    <v-card-text>
      <v-container>
        <booking-form
          v-if="bookingFormVisible"
          ref="bookingForm"
          :saveSignature="saveSignature"
          :clearSignature="clearSignature"
          :data="data"
        ></booking-form>
      </v-container>
    </v-card-text>
    <form-errors :errors="errors"></form-errors>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn :disabled="loading" color="blue darken-1" text @click="_close"
        >Cancel</v-btn
      >
      <v-btn
        :disabled="loading"
        v-if="Object.values(item).length > 0"
        color="blue darken-1"
        text
        @click="update"
        >Update</v-btn
      >
      <v-btn
        :disabled="loading"
        v-else
        color="blue darken-1"
        text
        @click="create"
        >Create</v-btn
      >
    </v-card-actions>
  </v-card>
</template>

<script>
import BookingForm from './BookingForm'
import { axios } from '../../../utils'
import FormErrors from '../../../components/FormErrors'
import { mapGetters } from 'vuex'
import { encryptBookingPayment } from '../../../utils/bookingPaymentEncryption'

export default {
  name: 'BookingDialog',
  data() {
    return {
      data: {
        id: null,
        client_name: '',
        business_name: '',
        website: '',
        abn: '',
        unit: '',
        street: '',
        suburb: '',
        suburbs: [],
        telephone: '',
        mobile: '',
        email: '',
        combinations: [],
        radius_coverages: [],
        sale_date: '',
        start_date: '',
        end_date: '',
        name_on_card: '',
        card_number: '',
        expiry_month: '',
        expiry_year: '',
        cvv: '',
        account_name: '',
        bsb_number: '',
        account_number: '',
        proof: '',
        sub_total: '',
        gst: '',
        total: '',
        deposit: '',
        balance: '',
        payment_type: '',
        month_beginning: '',
        total_months: '',
        monthly_amount: '',
        employee_id: null,
        lead_from: '',
        offer: '',
        last_edition_cost: '',
        last_edition_size: '',
        renewal: false,
        signature: '',
        notes: '',
        price: ''
      },
      dataLoaded: false,
      loading: false,
      errors: [],
      bookingFormVisible: true
    }
  },
  props: ['close', 'bookings', 'item'],
  components: {
    BookingForm,
    FormErrors
  },
  computed: {
    ...mapGetters({ img: 'getImg' })
  },
  async created() {
    if (this.item.id) {
      this.loading = true
      try {
        const { data } = await axios.get(
          `api/admin/booking-invoices/${this.item.id}`
        )
        this.handleBookingData(data)
      } catch (err) {
        this.handleErrors(err)
      } finally {
        this.loading = false
        this.dataLoaded = true
      }
    }
  },
  methods: {
    async createOrUpdate(url, requestData) {
      if (this.loading) return
      this.loading = true
      this.errors = []

      try {
        const encryptedRequest = await encryptBookingPayment(
          requestData,
          async () => {
            const { data } = await axios.post(
              'api/admin/booking-invoices/payment-encryption-key'
            )
            return data
          }
        )

        if (this.item.id) {
          await axios.put(url, encryptedRequest)
        } else {
          await axios.post(url, encryptedRequest)
        }
        this._close()
      } catch (err) {
        this.handleErrors(err)
      } finally {
        this.loading = false
      }
    },

    create() {
      if (
        this.$refs.bookingForm &&
        typeof this.$refs.bookingForm.autoCaptureArtifacts === 'function'
      ) {
        this.$refs.bookingForm.autoCaptureArtifacts()
      }
      const requestData = {
        img: this.img,
        ...this.data,
        combinations: this.data.combinations,
        radius_coverages: this.data.radius_coverages
      }
      this.createOrUpdate('api/admin/booking-invoices/create', requestData)
    },
    update() {
      if (
        this.$refs.bookingForm &&
        typeof this.$refs.bookingForm.autoCaptureArtifacts === 'function'
      ) {
        this.$refs.bookingForm.autoCaptureArtifacts()
      }
      const requestData = {
        img: this.img,
        ...this.data,
        combinations: this.data.combinations,
        radius_coverages: this.data.radius_coverages
      }
      this.createOrUpdate(
        `api/admin/booking-invoices/${this.item.id}`,
        requestData
      )
    },
    handleBookingData(value) {
      this.data = { ...this.data, ...value }
      this.data.suburbs = this.parseJsonArray(value.suburbs)
      this.data.combinations = this.parseJsonArray(value.combinations)
      this.data.radius_coverages = this.parseJsonArray(value.radius_coverages)
    },
    parseJsonArray(value) {
      if (Array.isArray(value)) {
        return value
      }
      if (!value) {
        return []
      }
      try {
        const parsed = JSON.parse(value)
        return Array.isArray(parsed) ? parsed : []
      } catch (err) {
        console.log(err)
        return []
      }
    },
    handleErrors(err) {
      if (err.response && err.response.status === 422) {
        this.errors = Object.values(err.response.data.errors)
      } else {
        this.errors = [
          [
            err.response
              ? 'Unable to save the booking. Please try again.'
              : 'Unable to encrypt or send payment details. Use HTTPS and try again.'
          ]
        ]
      }
    },
    saveSignature(value) {
      this.data.signature = value
    },
    clearSignature() {
      this.data.signature = null
    },
    _close() {
      this.clearData()
      this.close()
    },
    clearData() {
      this.data = {
        id: null,
        client_name: '',
        business_name: '',
        website: '',
        abn: '',
        unit: '',
        street: '',
        suburb: '',
        suburbs: [],
        telephone: '',
        mobile: '',
        email: '',
        combinations: [],
        radius_coverages: [],
        sale_date: '',
        start_date: '',
        end_date: '',
        name_on_card: '',
        card_number: '',
        expiry_month: '',
        expiry_year: '',
        cvv: '',
        account_name: '',
        bsb_number: '',
        account_number: '',
        proof: '',
        sub_total: '',
        gst: '',
        total: '',
        deposit: '',
        balance: '',
        payment_type: '',
        month_beginning: '',
        total_months: '',
        monthly_amount: '',
        employee_id: null,
        lead_from: '',
        offer: '',
        last_edition_cost: '',
        last_edition_size: '',
        renewal: false,
        signature: '',
        notes: '',
        price: ''
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
